import { useMutation, useQueryClient } from '@tanstack/react-query';

import { addonInstall } from '@api/sdk.gen';

import { withError, withInvalidate } from '@/react-tools/react-query';
import { RegistryId } from '@/react/portainer/registries/types/registry';

import { queryKeys } from '../query-keys';
import { PUBLIC_IMAGE_REGISTRY_ID } from '../InstallView/imagePullRegistry';

interface UpgradeAddonPayload {
  version: string;
  /**
   * Registry whose credentials pull the image. Re-sent on every upgrade because
   * the image reference is an editable chart value; undefined means the image is
   * public and is sent as an explicit 0, since the API reads an omitted field as
   * "keep what is installed".
   */
  imageRegistryId?: RegistryId;
  values: Record<string, unknown>;
}

// POST /addons/{id} performs helm upgrade --install, so the upgrade is a reuse
// of the install endpoint. Unlike useInstallAddon this does not touch team
// access (managed on the User Access tab) and invalidates the detail query too,
// so useAddon picks up the new lifecycle state as it polls.
export function useUpgradeAddon(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ version, imageRegistryId, values }: UpgradeAddonPayload) =>
      addonInstall({
        path: { id },
        body: {
          version: version || undefined,
          imageRegistryId: imageRegistryId ?? PUBLIC_IMAGE_REGISTRY_ID,
          values,
        },
      }),
    ...withInvalidate(queryClient, [queryKeys.list(), queryKeys.detail(id)]),
    ...withError('Unable to upgrade the add-on'),
  });
}
